import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

import { didNotFindOptionsShape, libraryShape, routerShape, userIdentityShape } from '@bibliocommons/bc-prop-types';
import { TextButton } from '@bibliocommons/deprecated-base-buttons';
import { QuestionMarkCircled } from '@bibliocommons/deprecated-base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import Context from '@bibliocommons/context';
import { bindAll } from '@bibliocommons/utils-react';

import EmbeddedChatWidget from 'app/components/shared/EmbeddedChatWidget';
import { FILTER_PREFIX, FILTER_SEPARATOR } from 'app/constants/BibConstants';
import { CHAT_WIDGET_ID } from 'app/constants/LibraryConstants';
import { SAVED_SEARCH_WORKFLOW } from 'app/constants/WorkflowConstants';
import getToggledFilter from 'app/helpers/facets/getToggledFilter';
import updateSearchQuery from 'app/helpers/search/updateSearchQuery';
import { activeFiltersShape, chatWidgetsShape, orderedSearchTypesShape } from 'app/shapes/search';
import AppliedFilters from '../AppliedFilters';
import ChatWidgetButton from '../ChatWidgetButton';
import DidNotFind from '../DidNotFind';
import DidYouMean from '../DidYouMean';
import ExternalSearchLink from '../ExternalSearchLink';
import MobileSearchBox from '../MobileSearchBox';
import SearchTitle from '../SearchTitle';
import ShelfAvailabilityTitle from '../ShelfAvailabilityTitle';

import './EmptySearchResult.scss';

export class EmptySearchResult extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  static supportedChatWidgetIds = [
    CHAT_WIDGET_ID.Q_WIDGET,
    CHAT_WIDGET_ID.LIB_ANSWERS,
    CHAT_WIDGET_ID.LIBRARY_H3LP,
    CHAT_WIDGET_ID.MOSIO,
    CHAT_WIDGET_ID.REF_CHATTER
  ];

  unapplyAllFilters() {
    const { router, searchType } = this.props;

    const query = updateSearchQuery({
      currentQuery: router.query,
      page: undefined,
      searchType
    });

    router.push({ query });
  }

  unapplyFilter(fieldId, currentFilter) {
    const { activeFilters, router, searchType } = this.props;

    const query = updateSearchQuery({
      activeFilters,
      currentQuery: router.query,
      searchType,
      page: undefined,
      ...getToggledFilter({
        activeFilters,
        currentFilter,
        fieldId,
        separator: FILTER_SEPARATOR,
        override: false,
        prefix: FILTER_PREFIX
      })
    });

    router.push({ query });
  }

  toggleIsFiltersPinned() {
    const { activeFilters, locked, router, searchType } = this.props;

    const query = updateSearchQuery({
      activeFilters,
      currentQuery: router.query,
      locked: locked ? undefined : 'true',
      searchType
    });

    router.push({ query });
  }

  renderActiveFilters() {
    const { activeFilters, locked, singleBranch } = this.props;

    if (!activeFilters.isEmpty()) {
      return (
        <div className="active-filters">
          <FormattedMessage id="empty_search_clear_filters_info" />
          <div className="applied-filters">
            <AppliedFilters
              appliedFilters={activeFilters}
              hasPinnedToggle={false}
              isPinned={locked}
              isSingleBranch={singleBranch}
              toggleIsPinned={this.toggleIsFiltersPinned}
              unapplyAllFilters={this.unapplyAllFilters}
              unapplyFilter={this.unapplyFilter}
            />
          </div>
        </div>
      );
    }

    return null;
  }

  renderStaticEmptySearchResults() {
    const { hasStaticEmptySearchResults, intl, query } = this.props;

    if (!hasStaticEmptySearchResults) {
      return null;
    }

    const staticEmptySearchContent = intl.formatMessage({
      id: 'static_empty_search_results_page'
    });

    if (staticEmptySearchContent) {
      const InjectQueryIntoHtml = staticEmptySearchContent.replace(/{query}/gi, query);

      return (
        <div className="static-empty-search-content">
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: InjectQueryIntoHtml }}
          />
        </div>
      );
    }

    return null;
  }

  renderSavedSearchLink(updateWorkflow) {
    return (
      <TextButton
        handleClick={() => updateWorkflow(SAVED_SEARCH_WORKFLOW)}
        className="save-search-link"
        data-test-id="save-search-link"
      >
        {this.props.intl.formatMessage({ id: 'save_search' })}
      </TextButton>
    );
  }

  renderSearchBox() {
    const { orderedSearchTypes, locked, query, searchType, activeFilters } = this.props;

    return (
      this.context.mobileApp && (
        <MobileSearchBox
          locked={locked}
          query={query}
          searchType={searchType}
          searchTypes={orderedSearchTypes}
          activeFilters={activeFilters}
        />
      )
    );
  }

  renderDidNotFind() {
    const { query, didNotFindOptions } = this.props;

    return (
      didNotFindOptions.get('isEnabled') && (
        <div className="empty-search-did-not-find">
          <DidNotFind query={query} didNotFindOptions={didNotFindOptions} />
        </div>
      )
    );
  }

  renderDidYouMean() {
    const { didYouMeanQuerySuggestion, didYouMeanResultCount } = this.props;
    return (
      <DidYouMean key="did-you-mean" querySuggestion={didYouMeanQuerySuggestion} resultCount={didYouMeanResultCount} />
    );
  }

  renderExternalSearch() {
    const { query, searchType, showExternalSearchLink } = this.props;

    return showExternalSearchLink && <ExternalSearchLink query={query} searchType={searchType} formatted />;
  }

  renderEmbeddedChatWidget() {
    const { chatWidgets } = this.props;
    if (!chatWidgets) {
      return null;
    }

    const widgets = EmptySearchResult.supportedChatWidgetIds
      .filter(chatWidgetId => {
        return chatWidgets.get(chatWidgetId) && chatWidgets.getIn([chatWidgetId, 'enabled']);
      })
      .map(chatWidgetId => {
        return <EmbeddedChatWidget key={chatWidgetId} widget={chatWidgetId} />;
      });

    return widgets.length ? <div className="empty-search-embedded-chat-widgets">{widgets}</div> : null;
  }

  renderChatWidgetButton() {
    const enabledWidget = this.props.chatWidgets.findLastKey(widget => widget.get('enabled'));

    if (enabledWidget && EmptySearchResult.supportedChatWidgetIds.includes(enabledWidget)) {
      return (
        <span className="empty-search-chat-widget">
          <ChatWidgetButton widget={enabledWidget} />
        </span>
      );
    }

    return null;
  }

  renderSearchTipsLink() {
    const { library } = this.props;
    const searchTipsLink = library.get('searchTipsLink');

    if (searchTipsLink) {
      return (
        <a className="search-tips" href={searchTipsLink} target="_blank" rel="noopener noreferrer">
          <span aria-hidden>
            <QuestionMarkCircled />
            <FormattedMessage id="search_tips_link_text" />
          </span>
          <ScreenReaderMessage>
            <FormattedMessage
              id="ada_opens_a_new_tab"
              values={{
                linkText: <FormattedMessage id="search_tips_link_text" />
              }}
            />
          </ScreenReaderMessage>
        </a>
      );
    }
    return null;
  }

  renderSubtitle() {
    const { query, shelfName } = this.props;

    if (shelfName) {
      return <FormattedMessage id="no_available_items" tagName="h4" />;
    }

    return (
      query && <FormattedMessage id="Search.empty_search" tagName="h4" values={{ query: <strong>{query}</strong> }} />
    );
  }

  renderMainContents() {
    return (
      <div className="empty-search-main-content">
        <div className="empty-search-sub-title">
          {this.renderSubtitle()}
          {this.renderDidYouMean()}
          {this.renderActiveFilters()}
          {this.renderExternalSearch()}
        </div>
        {this.renderSearchTools()}
      </div>
    );
  }

  renderSecondaryContents() {
    return (
      !this.props.didNotFindOptions.get('isEnabled') && (
        <div className="empty-search-secondary-content">
          {this.renderStaticEmptySearchResults()}
          {this.renderEmbeddedChatWidget()}
        </div>
      )
    );
  }

  renderSearchTools() {
    return (
      this.props.didNotFindOptions.get('isEnabled') && (
        <div className="empty-search-help-tools">
          {this.renderSearchTipsLink()}
          {this.renderChatWidgetButton()}
        </div>
      )
    );
  }

  renderSearchTitleHeader() {
    const {
      query,
      titleKey,
      didYouMeanQuerySuggestion,
      didYouMeanResultCount,
      searchType,
      broadened,
      broadenedSearchType,
      showExternalSearchLink,
      rssURL,
      customEdit,
      suppress,
      didNotFindOptions,
      currentUser,
      shelfName
    } = this.props;

    if (shelfName) {
      return <ShelfAvailabilityTitle shelfName={shelfName} userId={currentUser.get('id')} />;
    }

    return (
      <SearchTitle
        library={this.props.library}
        broadened={broadened}
        broadenedSearchType={broadenedSearchType}
        customEdit={customEdit}
        didYouMeanQuerySuggestion={didYouMeanQuerySuggestion}
        didYouMeanResultCount={didYouMeanResultCount}
        showDidYouMean={false}
        id="searchTitle"
        query={query}
        searchType={searchType}
        showExternalSearchLink={showExternalSearchLink}
        suppress={suppress}
        titleKey={titleKey}
        rssURL={rssURL}
        didNotFindOptions={didNotFindOptions}
      />
    );
  }

  render() {
    return (
      <div className="cp-empty-search-result">
        {this.renderSearchBox()}
        {this.renderSearchTitleHeader()}
        {this.renderMainContents()}
        {this.renderSecondaryContents()}
        {this.renderDidNotFind()}
      </div>
    );
  }
}

EmptySearchResult.displayName = 'EmptySearchResult';
EmptySearchResult.propTypes = {
  query: PropTypes.string,
  titleKey: PropTypes.string,
  activeFilters: activeFiltersShape.isRequired,
  chatWidgets: chatWidgetsShape.isRequired,
  currentUser: userIdentityShape,
  orderedSearchTypes: orderedSearchTypesShape.isRequired,
  didYouMeanQuerySuggestion: PropTypes.string,
  didYouMeanResultCount: PropTypes.number,

  hasStaticEmptySearchResults: PropTypes.bool.isRequired,
  searchType: PropTypes.string,
  broadened: PropTypes.bool,
  broadenedSearchType: PropTypes.string,
  showExternalSearchLink: PropTypes.bool,
  locked: PropTypes.bool,
  rssURL: PropTypes.string,
  customEdit: PropTypes.bool,
  suppress: PropTypes.bool,
  didNotFindOptions: didNotFindOptionsShape,
  singleBranch: PropTypes.bool,
  shelfName: PropTypes.string,
  library: libraryShape,
  router: routerShape.isRequired
};

EmptySearchResult.contextType = Context;

export default injectIntl(EmptySearchResult);
